/* eslint-disable no-duplicate-imports */
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { Row } from '@tanstack/react-table';
import classname from 'classnames';
import { action, observable, toJS, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Toaster } from 'sonner';
import getOrderHistoryAction, { GetOrderHistoryInput, IOrderHistoryTableData } from '../../actions/order-history/get-order-history.action';
import { Label } from '../../components/label';
import { ModalProvider } from '../../components/modal';
import {
    ERROR_CODE_SERVER_ERROR_INDEX,
    ERROR_CODE_SERVER_NOTFOUND_INDEX,
    OPEN_STATUS,
    ORDER_HISTORY_FILTER_RANGE,
    ORDER_HISTORY_ORDER_TYPE
} from '../../configuration';
import { ILocalizationState } from '../../states/localization-state';
import { exportToExcel } from '../../utilities/export-to-xlsx';
import { runActionAndHandleAuth } from '../../utilities/is-authenticated';
import { _showToast } from '../../utilities/show-toast';
import { IOrderHistoryData } from './order-history.data';
import { IOrderHistoryProps } from './order-history.props.autogenerated';

export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
    orderHistoryOrderCountLabel?: React.ReactNode;
    extraActions?: React.ReactNode;
}

export interface IOrderHistoryViewState {
    isDataReady: boolean;
    hasError: boolean;
    errorCode?: string;
    errorMessage?: string;
}

export interface IOrderHistoryViewProps extends IOrderHistoryProps<IOrderHistoryData> {
    orderHistoryProps: IModuleProps;
    pageSize: number;
    emptyMessage?: React.ReactNode;
    header: IHeader;
    viewState: IOrderHistoryViewState;
    orderHistoryTableData: IOrderHistoryTableData[] | undefined;
    orderHistoryReturnCodes: number[];
    localizationState: ILocalizationState;
    excelConfig: string[];
    props: IOrderHistoryProps<IOrderHistoryData>;
    handleOrderHistoryTableData: (numMonths: Number, currentOrderView: string) => Promise<void>;
    loading?: React.ReactNode;
    isDataReady: boolean;
    exportToExcel: (
        tableRows: Row<IOrderHistoryTableData>[],
        localizationState: ILocalizationState,
        resources: any,
        selectedConfigArray: string[]
    ) => void;
}

/**
 *
 * OrderHistory component.
 * @extends {React.Component<IOrderHistoryProps<IOrderHistoryData>>}
 */
@observer
class OrderHistory extends React.Component<IOrderHistoryProps<IOrderHistoryData>> {
    @observable private viewState!: IOrderHistoryViewState;
    @observable private orderHistoryTableData!: IOrderHistoryTableData[] | undefined;
    @observable private orderHistoryReturnCodes!: number[];
    @observable public fetchError: any = false;

    constructor(props: IOrderHistoryProps<IOrderHistoryData>) {
        super(props);
        this.initState();
    }

    public async componentDidMount() {
        const serverResult = this.props.data.getOrderHistory?.result;

        if (serverResult) {
            if (serverResult.error) {
                this.setFetchError(true);
            } else {
                this.setOrderHistoryTableData(serverResult.Orders);
                this.setOrderHistoryReturnCode(serverResult.ReturnCodes);
            }
        } else {
            const url = new URL(window.location.href);
            const range = url.searchParams.get(ORDER_HISTORY_FILTER_RANGE) ?? '';
            const currentOrderType = url.searchParams.get(ORDER_HISTORY_ORDER_TYPE) ?? OPEN_STATUS;

            await this.__getOrderHistoryTableData(Number(range), currentOrderType);
        }
        if (this.props.data.localizationState) {
            await this.props.data.localizationState;
        }

        this.setDataReady();
    }

    @computed
    get excelConfig(): string[] {
        const raw = toJS(this.props.data?.getExcelConfig.result as any);
        if (!raw) {
            return [];
        }
        return raw.value;
    }

    @action
    public setDataReady = () => {
        this.viewState.isDataReady = true;
    };

    @action
    public setFetchError(fetchError: boolean) {
        this.fetchError = fetchError;
    }

    @action
    public setOrderHistoryTableData(orderHistoryTableData: any) {
        this.orderHistoryTableData = toJS(orderHistoryTableData);
    }

    @action
    public setOrderHistoryReturnCode(orderHistoryData: any) {
        this.orderHistoryReturnCodes = toJS(orderHistoryData);
    }

    private __getOrderHistoryTableData = async (numMonths: Number, currentOrderView: string) => {
        const ctx = this.props.context.actionContext;

        const actionInput = new GetOrderHistoryInput(ctx, currentOrderView, numMonths);
        const orderHistory = await runActionAndHandleAuth(() => getOrderHistoryAction(actionInput, ctx), this.props.context.request.user);

        if (orderHistory) {
            if (
                (orderHistory.error && Number(String(orderHistory.error.errorCode).charAt(0)) === ERROR_CODE_SERVER_ERROR_INDEX) ||
                (orderHistory.error && Number(String(orderHistory.error.errorCode)) === ERROR_CODE_SERVER_NOTFOUND_INDEX)
            ) {
                _showToast(this.props.resources.globalErrorHeading, this.props.resources.globalErrorServiceNotAvailable, 'error');
                this.setFetchError(true);
            } else {
                this.setOrderHistoryTableData(orderHistory.Orders);
                this.setOrderHistoryReturnCode(orderHistory.ReturnCodes);
            }
        }
    };

    public render(): JSX.Element {
        const localizationState = toJS(this.props.data.localizationState).result as ILocalizationState;

        const {
            config: { heading, pageSize, className },
            resources: { orderHistoryNoOrderHistoryLabel, loadingLabel }
        } = this.props;
        const { isDataReady } = this.viewState;

        let viewProps: IOrderHistoryViewProps = {
            ...this.props,
            exportToExcel: exportToExcel,
            viewState: this.viewState,
            orderHistoryProps: {
                moduleProps: this.props,
                className: classname('ms-order-history', className)
            },
            header: {
                headerProps: { className: 'ms-order-history__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        {...heading}
                        className='ms-order-history__heading'
                        text={heading.text}
                        tag={heading.tag || 'h2'}
                        editProps={{ onEdit: this.handleOrderHistoryHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            pageSize: pageSize || 10,
            orderHistoryTableData: this.orderHistoryTableData,
            orderHistoryReturnCodes: this.orderHistoryReturnCodes,
            handleOrderHistoryTableData: (numMonths: Number, currentOrderView: string) => {
                return this.__getOrderHistoryTableData(numMonths, currentOrderView);
            },
            localizationState: localizationState,
            excelConfig: this.excelConfig,
            props: this.props,
            config: this.props.config,
            isDataReady: isDataReady
        };

        if (!isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <Label className='ms-order-history__loading' label={loadingLabel} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.orderHistoryTableData || this.orderHistoryTableData.length === 0) {
            if (this.viewState?.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_AuthorizationFailed') {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className='ms-order-history__empty-message'>{this.viewState.errorMessage}</p>
                };
            } else {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className='ms-order-history__empty-message'>{orderHistoryNoOrderHistoryLabel}</p>
                };
            }
        }

        return (
            <>
                <Toaster
                    richColors
                    position='top-right'
                    toastOptions={{
                        classNames: {
                            default: 'toast-light',
                            error: 'toast-danger',
                            success: 'toast-success',
                            warning: 'toast-warning',
                            info: 'toast-info'
                        }
                    }}
                />
                {this.fetchError === true ? (
                    <>
                        <div className='alert alert-warning' role='alert'>
                            <span className='ms-order-history__alert-heading'>{this.props.resources.globalErrorServiceNotAvailable}</span>
                        </div>
                    </>
                ) : (
                    <ModalProvider props={this.props}>{this.props.renderView(viewProps) as React.ReactElement}</ModalProvider>
                )}
            </>
        );
    }

    private readonly initState = () => {
        this.viewState = {
            isDataReady: false,
            hasError: false
        };
    };

    public handleOrderHistoryHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);
}

export default OrderHistory;
